// The Storybook version of an album: the same photos, told as a story. Three ways to go through it, each the
// reader's pick (kept on this device): chapters to scroll, a book to page through, or prints on a table. Every one
// is laid out here from what the album already holds (its photos in their order, their dates, captions and who's
// tagged, and the keeper's description). Nothing is written for it and no photo is changed: the storybook only
// frames, orders and moves them, and the original is always a tap away.
import type { PageEdits, PHOTO_PAGE } from '../../shared/printDesign'
import type { Album, AlbumPhoto } from './albums'
import { peopleIn } from './albums'
import type { OnThisDayStory, OnThisDayStoryYear } from './home'
import { remember, remembered } from './remember'

export const STORY_VIEWS = ['book', 'chapters', 'table'] as const
export type StoryView = (typeof STORY_VIEWS)[number]
export const STORY_VIEW_NAMES: Record<StoryView, string> = { chapters: 'Chapters', book: 'Book', table: 'Table' }
// The book opens first. The key changed when it became the default, so an earlier pick of chapters doesn't hide it.
const VIEW_KEY = 'gf.story-view.2'
export const savedStoryView = () => remembered(VIEW_KEY, STORY_VIEWS, 'book')
export const saveStoryView = (view: StoryView) => remember(VIEW_KEY, view)

// The year a photo was taken, when anyone knows it.
export const yearOf = (photo: Pick<AlbumPhoto, 'takenOn'>) => photo.takenOn?.slice(0, 4) || null

// A chapter is a year: its photos in the album's order, and everyone tagged in them. Years run oldest first, and the
// photos nobody has dated close the story in a chapter of their own rather than being given a year.
export type Chapter = { year: string | null; photos: AlbumPhoto[]; people: ReturnType<typeof peopleIn> }
export function chapters(photos: AlbumPhoto[]): Chapter[] {
  const byYear = new Map<string | null, AlbumPhoto[]>()
  for (const photo of photos) {
    const year = yearOf(photo)
    byYear.set(year, [...(byYear.get(year) ?? []), photo])
  }
  return [...byYear]
    .sort(([a], [b]) => (a === null ? 1 : b === null ? -1 : a.localeCompare(b)))
    .map(([year, photos]) => ({ year, photos, people: peopleIn(photos) }))
}

// The photo a story opens on: the keeper's cover, else the album's first photo.
export const coverOf = (album: Pick<Album, 'photos' | 'coverPhotoId'>) =>
  album.photos.find((x) => x.id === album.coverPhotoId) ?? album.photos[0] ?? null

export const wide = (photo: Pick<AlbumPhoto, 'width' | 'height'>) => photo.width >= photo.height * 1.15
export const tall = (photo: Pick<AlbumPhoto, 'width' | 'height'>) => photo.height >= photo.width * 1.15

// The book's pages: a cover, the keeper's words when there are any, then each chapter's photos laid out a page at a
// time, and the people in it at the end. A page holds up to four photos, a chapter's photos shared as evenly as they
// go (five make a page of three and a page of two, not four and one): one is centered, two sit one over the other,
// three are two above one centered below, and four are a square of two by two. A printed book's look can hold fewer,
// and a printed page can be given its own choices (edits, by the photo it starts with): that page always starts with
// its photo, holds its own number of photos, and the photos after it are shared out again until the next such page.
export const PAGE_MOST = 4
export type PhotoPageEdits = PageEdits<typeof PHOTO_PAGE>
export type BookPage =
  | { kind: 'cover' }
  | { kind: 'words' }
  | {
      kind: 'photos'
      year: string | null
      opens: boolean
      photos: AlbumPhoto[]
      edges?: (typeof PHOTO_PAGE.edges)[number]
    }
  | { kind: 'people' }
export function bookPages(
  album: Pick<Album, 'description' | 'photos'>,
  most: number = PAGE_MOST,
  edits: PhotoPageEdits = {},
): BookPage[] {
  const pages: BookPage[] = [{ kind: 'cover' }]
  if (album.description.trim()) pages.push({ kind: 'words' })
  for (const chapter of chapters(album.photos)) {
    const runs: AlbumPhoto[][] = []
    for (const photo of chapter.photos)
      if (runs.length && !edits[photo.id]) runs.at(-1)!.push(photo)
      else runs.push([photo])
    for (const run of runs) {
      const edit = edits[run[0]!.id]
      const own = edit?.most ? [run.slice(0, edit.most)] : []
      const groups = [...own, ...shared(run.slice(own[0]?.length ?? 0), most)]
      groups.forEach((photos, i) =>
        pages.push({
          kind: 'photos',
          year: chapter.year,
          opens: run === runs[0] && i === 0,
          photos,
          ...(i === 0 && edit?.edges ? { edges: edit.edges } : {}),
        }),
      )
    }
  }
  if (peopleIn(album.photos).length) pages.push({ kind: 'people' })
  return pages
}

// Photos shared over as few pages as hold them, the earlier pages taking any one left over, so a run never ends on a
// page emptier than the rest.
function shared(photos: AlbumPhoto[], most: number): AlbumPhoto[][] {
  const n = photos.length
  const count = Math.ceil(n / most)
  const pages: AlbumPhoto[][] = []
  let at = 0
  for (let page = 0; page < count; page++) {
    const take = Math.floor(n / count) + (page < n % count ? 1 : 0)
    pages.push(photos.slice(at, at + take))
    at += take
  }
  return pages
}

// Prints on a table. A spot is where a print lies (its top left, in pixels of the table) and how far it's turned.
export type Spot = { x: number; y: number; turn: number; width: number }
export type Size = { width: number; height: number }
// The white edge of a print, and its deeper bottom edge, as a share of its width.
export const PRINT_EDGE = 0.04
export const PRINT_FOOT = 0.12
export const printHeight = (photo: Size, width: number) =>
  width * (1 - 2 * PRINT_EDGE) * (photo.height / photo.width) + width * (PRINT_EDGE + PRINT_FOOT)
// How many prints lie on the table at once; more would be a heap no one could look through.
export const TABLE_MAX = 30
// A number between 0 and 1 that is always the same for the same seed, so prints fall the same way every visit.
const scatter = (seed: number) => {
  const x = Math.sin(seed * 9301 + 49297) * 233280
  return x - Math.floor(x)
}

// Spread across the table, each at a slight angle, kept fully on it.
export function spread(photos: Size[], table: Size): Spot[] {
  const base = Math.min(table.width, table.height) / (photos.length > 12 ? 2.6 : 2.1)
  return photos.map((photo, i) => {
    const width = Math.round(Math.min(table.width * 0.62, wide(photo) ? base * 1.25 : base))
    const height = printHeight(photo, width)
    return {
      width,
      x: Math.round(8 + scatter(i + 1) * Math.max(0, table.width - width - 16)),
      y: Math.round(8 + scatter(i + 21) * Math.max(0, table.height - height - 16)),
      turn: Math.round((scatter(i + 7) - 0.5) * 180) / 10,
    }
  })
}

// Gathered into a fan across the middle of the table: a person's photos, or one year's.
export function fan(photos: Size[], table: Size): Spot[] {
  const n = photos.length
  const width = Math.round(Math.min(table.width * (n > 6 ? 0.36 : 0.44), table.height * 0.5))
  return photos.map((photo, i) => {
    const t = n === 1 ? 0.5 : i / (n - 1)
    const height = printHeight(photo, width)
    return {
      width,
      x: Math.round(10 + t * Math.max(0, table.width - width - 20)),
      y: Math.round(table.height / 2 - height / 2 - Math.sin(t * Math.PI) * 24),
      turn: Math.round((t - 0.5) * (n > 1 ? 22 : 0) * 10) / 10,
    }
  })
}

// What a chapter of photos with no date is called.
export const OTHER_TIMES = 'Other times'

// One row a year, oldest at the top, undated last. A row holds every one of its prints, overlapping more as they
// grow in number; labelX is where the prints start, leaving room for the year.
export function rows(groups: Size[][], table: Size, labelX: number): { top: number; height: number; spots: Spot[] }[] {
  const rowHeight = table.height / Math.max(1, groups.length)
  return groups.map((photos, g) => {
    const top = Math.round(g * rowHeight)
    const tallest = Math.max(1, rowHeight - 28)
    const spots = photos.map((photo, i) => {
      const width = Math.round(Math.min(table.width * 0.4, (tallest / printHeight(photo, 100)) * 100))
      const room = table.width - labelX - width - 8
      const step = photos.length > 1 ? Math.min(width * 0.8, room / (photos.length - 1)) : 0
      return {
        width,
        x: Math.round(labelX + i * Math.max(0, step)),
        y: top + 6 + Math.round(scatter(i + g * 5) * 4),
        turn: Math.round((scatter(i + g * 3) - 0.5) * 70) / 10,
      }
    })
    return { top, height: Math.round(rowHeight), spots }
  })
}

// On this day as a story: a chapter a year, oldest first, holding that day's photos (shaped like an album's, with who
// added each) and moments, and everyone in either. Kinds whose sections are switched off are left out, and so is a
// year left with nothing.
export type StoryPhoto = AlbumPhoto & { by: string }
export type DayChapter = {
  year: number
  photos: StoryPhoto[]
  morePhotos: number
  moments: OnThisDayStoryYear['moments']['shown']
  moreMoments: number
  people: ReturnType<typeof peopleIn>
}
export function dayChapters(story: OnThisDayStory, on: { photos: boolean; moments: boolean }): DayChapter[] {
  return story.years
    .map((year) => {
      const photos = on.photos ? year.photos.shown.map((x) => ({ ...x, canEdit: false, tags: [] })) : []
      const moments = on.moments ? year.moments.shown : []
      return {
        year: year.year,
        photos,
        morePhotos: on.photos ? year.photos.count - photos.length : 0,
        moments,
        moreMoments: on.moments ? year.moments.count - moments.length : 0,
        people: peopleIn([...photos, ...moments]),
      }
    })
    .filter((x) => x.photos.length + x.moments.length > 0)
}

// Playing the chapters: the story scrolls itself, with a rhythm rather than a steady crawl. At each stop (the cover, a
// chapter's opening, each photo or moment, the ending) it lingers, drifting slowly so the photo settles and
// grows, then slides quickly on to the next. A stop is where it sits in the scrolling story, in pixels. A photo's caption
// types itself out as it arrives, to draw the eye to it, then stays long enough to read; a photo with no words moves
// on sooner. The ending types the keeper's words the same way, and is read through to the bottom.
export type PlayStop = {
  kind: 'cover' | 'chapter' | 'item' | 'end'
  top: number
  height: number
  // How many characters of caption (or of the keeper's words) it types out.
  chars?: number
}
export type PlayStep = { from: number; to: number; ms: number; pace: 'slide' | 'linger'; stop: number }
const SLIDE = { min: 650, max: 1100 }
const LINGER = { cover: 1200, chapter: 2800, item: 2000, end: 4000 }
// How fast a caption types, and how long it then stays to be read: a little longer the longer it is.
export const TYPE_MS = 28
const reading = (chars: number) => Math.min(6000, Math.max(2500, 1500 + chars * 45))
export const lingerOn = (chars = 0) => (chars ? chars * TYPE_MS + reading(chars) : LINGER.item)

// It starts from wherever the reader is (start), at the first stop not yet passed. A reader who asks for less motion gets
// the same pauses, but each move is a cut rather than a slide, and nothing drifts.
export function playPlan(stops: PlayStop[], view: number, end: number, start = 0, still = false): PlayStep[] {
  const steps: PlayStep[] = []
  const at = (y: number) => Math.round(Math.min(end, Math.max(0, y)))
  let here = at(start)
  stops.forEach((stop, i) => {
    // Where it arrives and how far it drifts while it lingers. A photo arrives a little below the middle of the screen
    // and drifts up through it; the ending is read through to its bottom.
    const [arrive, drift, linger] =
      stop.kind === 'cover'
        ? [0, 0, LINGER.cover]
        : stop.kind === 'chapter'
          ? [stop.top, view * 0.25, LINGER.chapter]
          : stop.kind === 'end'
            ? [stop.top, Math.max(0, stop.height - view), Math.max(LINGER.end, stop.chars ? lingerOn(stop.chars) : 0)]
            : [stop.top + stop.height / 2 - view * 0.42, view * 0.14, lingerOn(stop.chars)]
    if (arrive < here - view * 0.5) return
    // Never backwards: a stop the lingering already passed is arrived at where the story is.
    const from = at(Math.max(arrive, here))
    if (from !== here) {
      const ms = still ? 0 : Math.round(Math.min(SLIDE.max, Math.max(SLIDE.min, ((from - here) / view) * 500)))
      steps.push({ from: here, to: from, ms, pace: 'slide', stop: i })
    }
    const to = still ? from : at(from + drift)
    steps.push({ from, to, ms: linger, pace: 'linger', stop: i })
    here = to
  })
  return steps
}

// The slide starts gently, rushes and settles; the linger barely slows as it ends.
export const ease = {
  slide: (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2),
  linger: (t: number) => Math.sin((t * Math.PI) / 2) * 0.35 + t * 0.65,
}
